import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'helpers/api';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';

const RELATIONSHIP_OPTIONS = [
  { value: '', label: 'Select relationship (optional)' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'caregiver', label: 'Caregiver' },
  { value: 'friend', label: 'Friend' },
  { value: 'other', label: 'Other' }
];

/**
 * Modal opened from the caregiver detail page to assign one or more patients to
 * a SharedLiving caregiver. Admin first picks facilities to source patients
 * from, then picks patients. Single + bulk via /athome/bulk-assign-patients
 * (UI-013).
 */
const AssignPatientModal = ({ show, setShow, userId, alreadyAssignedIds = [], onSaved }) => {
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [patientOptions, setPatientOptions] = useState([]);
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [relationship, setRelationship] = useState('');
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    // Reset on open.
    setSelectedFacilities([]);
    setPatientOptions([]);
    setSelectedPatients([]);
    setRelationship('');

    const facilities = JSON.parse(localStorage.getItem('facilitiesData')) || [];
    setFacilityOptions(facilities.map(f => ({ value: f.value, label: f.key })));
  }, [show]);

  useEffect(() => {
    const facilityIds = selectedFacilities.map(f => f.value);
    if (facilityIds.length === 0) {
      setPatientOptions([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoadingPatients(true);
      try {
        const tokenStr = localStorage.getItem('accessToken');
        const lists = await Promise.all(
          facilityIds.map(async id => {
            try {
              const { data } = await axios.get(
                `${process.env.REACT_APP_SERVER_URL}/patients/list-all-patients/${id}`,
                { headers: { Authorization: `Bearer ${tokenStr}` } }
              );
              return data?.patients?.data || [];
            } catch {
              return [];
            }
          })
        );

        if (cancelled) return;
        const seen = new Set();
        const merged = [];
        lists.flat().forEach(p => {
          if (Number(p.is_active) !== 1) return;
          if (alreadyAssignedIds.map(String).includes(String(p.id))) return;
          if (seen.has(String(p.id))) return;
          seen.add(String(p.id));
          merged.push({
            value: p.id,
            label: `${p.last_name}, ${p.first_name}${p.dob ? ` (${p.dob})` : ''}`
          });
        });
        merged.sort((a, b) => a.label.localeCompare(b.label));
        setPatientOptions(merged);
      } finally {
        if (!cancelled) setLoadingPatients(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedFacilities, alreadyAssignedIds]);

  const handleSave = async () => {
    const patientIds = selectedPatients.map(p => p.value);
    if (patientIds.length === 0) {
      toast.error('Select at least one patient to assign.');
      return;
    }

    setSaving(true);
    try {
      const tokenStr = localStorage.getItem('accessToken');
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/athome/bulk-assign-patients`,
        {
          user_id: userId,
          patient_ids: patientIds,
          ...(relationship ? { relationship } : {})
        },
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );
      toast.success(
        `${patientIds.length} patient${patientIds.length > 1 ? 's' : ''} assigned successfully`
      );
      setShow(false);
      onSaved && onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Unable to assign patients');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={() => setShow(false)} size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="h5">Assign Patients</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Facilities</Form.Label>
          <ThemedSelect
            isMulti
            options={facilityOptions}
            value={selectedFacilities}
            onChange={value => setSelectedFacilities(value || [])}
            placeholder="Select facilities to pick patients from"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Patients</Form.Label>
          <ThemedSelect
            isMulti
            isLoading={loadingPatients}
            options={patientOptions}
            value={selectedPatients}
            onChange={value => setSelectedPatients(value || [])}
            placeholder={
              selectedFacilities.length === 0
                ? 'Select a facility first'
                : 'Select patients to assign'
            }
          />
        </Form.Group>
        <Form.Group style={{ maxWidth: '320px' }}>
          <Form.Label>Relationship</Form.Label>
          <ThemedSelect
            options={RELATIONSHIP_OPTIONS}
            value={RELATIONSHIP_OPTIONS.find(o => o.value === relationship) || null}
            onChange={opt => setRelationship(opt?.value || '')}
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShow(false)}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || selectedPatients.length === 0}>
          {saving && <Spinner animation="border" size="sm" className="me-2" />}
          Assign
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignPatientModal;
